import React, { useState } from 'react';
import { CustomInput } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import { colors, fonts } from 'shared/defaultTheme';
import { ReactComponent as ClearIcon } from 'assets/icon-close-round.svg';
import { ReactComponent as EyeIcon } from 'assets/icon-visible.svg';
import { ReactComponent as EyeNotIcon } from 'assets/icon-not-visible.svg';

export const TextField = React.forwardRef(
  (
    {
      className,
      placeholder,
      type,
      name,
      value,
      step,
      pattern,
      onChange,
      onBlur,
      borderRadius,
      inputClassName,
      Icon,
      withButtonClear,
      onButtonClearClick,
      buttonClassName,
      error,
      rightIcon,
      isReadOnly,
      onKeyDown,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const onInternalBlur = () => {
      setTimeout(() => {
        setIsFocused(false);
      }, 500);
      if (onBlur) {
        onBlur();
      }
    };
    const onClearInput = async () => {
      if (onButtonClearClick) {
        onButtonClearClick();
      } else {
        onChange('');
      }
    };

    return (
      <div
        className={classNames(
          {
            'form__form-group-field': true,
            'form__form-group-field--focus': isFocused,
            'form__form-group-field--error': error,
            'form__form-group-field--disabled': isReadOnly,
          },
          className
        )}
        style={{ borderRadius }}
      >
        {Icon && (
          <div className="form__form-group-icon">
            <Icon />
          </div>
        )}
        <input
          className={classNames({ input__text: true }, inputClassName)}
          placeholder={placeholder}
          type={type}
          name={name}
          value={value}
          step={step}
          pattern={pattern}
          onChange={onChange}
          readOnly={isReadOnly}
          disabled={isReadOnly}
          min={1}
          onFocus={() => setIsFocused(true)}
          onBlur={onInternalBlur}
          ref={ref}
          style={{ borderRadius }}
          onKeyDown={onKeyDown}
        />
        {withButtonClear && (
          <button
            className={classNames(
              ['form__form-group-button form__form-group-button-close'],
              buttonClassName
            )}
            onClick={onClearInput}
            type="button"
          >
            {isFocused && (
              <ClearIcon
                fill={error ? colors.error : colors.suvaGrey}
                className="form__input-clear-icon"
              />
            )}
          </button>
        )}
        {rightIcon && <div className={classNames(['form__form-group-button'])}>{rightIcon}</div>}
      </div>
    );
  }
);

TextField.defaultProps = {
  className: '',
  placeholder: '',
  type: 'text',
  name: '',
  value: undefined,
  step: undefined,
  pattern: undefined,
  onChange: () => null,
  onKeyDown: () => null,
  onButtonClearClick: undefined,
  onBlur: undefined,
  borderRadius: 4,
  inputClassName: '',
  buttonClassName: '',
  Icon: undefined,
  withButtonClear: true,
  error: false,
  rightIcon: undefined,
  isReadOnly: false,
};

TextField.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pattern: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  onButtonClearClick: PropTypes.func,
  borderRadius: PropTypes.number,
  inputClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  Icon: PropTypes.func,
  withButtonClear: PropTypes.bool,
  error: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  rightIcon: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export const PasswordField = React.forwardRef(
  (
    { className, placeholder, name, value, onChange, borderRadius, inputClassName, Icon, error },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const onInternalBlur = () => {
      setTimeout(() => {
        setIsFocused(false);
      }, 500);
    };
    return (
      <div
        className={classNames(
          {
            'form__form-group-field': true,
            'form__form-group-field--focus': isFocused,
            'form__form-group-field--error': error,
          },
          className
        )}
        style={{ borderRadius }}
      >
        {Icon && (
          <div className="form__form-group-icon">
            <Icon />
          </div>
        )}
        <input
          className={classNames({ input__text: true }, inputClassName)}
          placeholder={placeholder}
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={onInternalBlur}
          ref={ref}
          style={{ borderRadius }}
        />
        <button
          className="form__form-group-button"
          onClick={() => setShowPassword(!showPassword)}
          type="button"
        >
          {!showPassword ? (
            <EyeNotIcon fill={error ? colors.error : colors.suvaGrey} />
          ) : (
            <EyeIcon fill={error ? colors.error : colors.suvaGrey} />
          )}
        </button>
      </div>
    );
  }
);

PasswordField.defaultProps = {
  className: '',
  placeholder: '',
  name: '',
  value: undefined,
  onChange: () => null,
  borderRadius: 4,
  inputClassName: '',
  Icon: undefined,
  error: false,
};

PasswordField.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  onChange: PropTypes.func,
  borderRadius: PropTypes.number,
  inputClassName: PropTypes.string,
  Icon: PropTypes.func,
  error: PropTypes.bool,
};

const SwitchWrapper = styled.div`
  span {
    font-family: ${fonts.medium}, sans-serif;
    font-size: 14px;
    line-height: 16px;
    color: ${({ color, textcolor }) => textcolor || color} !important;
  }
`;

const CustomSwitch = styled(CustomInput)`
  label {
    font-family: ${fonts.medium}, sans-serif;
    font-size: 14px;
    line-height: 16px;
    color: ${({ color, textcolor }) => textcolor || color} !important;
    ::before {
      border: 2px ${({ color }) => color} solid !important;
    }
    ::after {
      border: 2px ${({ color }) => color} solid !important;
    }
  }
`;

export const Switch = ({
  id,
  classNameMain,
  className,
  label,
  color,
  textColor,
  value,
  onChange,
  disabled,
}) => {
  return (
    <SwitchWrapper
      color={color}
      textcolor={textColor}
      className={classNames(['d-flex align-items-center', classNameMain])}
    >
      <CustomSwitch
        id={id}
        className={classNames(['form-switch-input', 'custom-switch', className])}
        type="checkbox"
        color={color}
        textcolor={textColor}
        checked={value}
        onChange={(e) => {
          onChange(!e.target.checked);
        }}
        disabled={disabled}
      />
      <span>{label}</span>
    </SwitchWrapper>
  );
};

Switch.defaultProps = {
  classNameMain: '',
  className: '',
  label: '',
  color: undefined,
  value: false,
  textColor: undefined,
  disabled: false,
};

Switch.propTypes = {
  id: PropTypes.string.isRequired,
  classNameMain: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  color: PropTypes.string,
  textColor: PropTypes.string,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
